const { useLayoutEffect, useRef, useState } = require('react');
const T = require('prop-types');
const { default: styled } = require('styled-components');
const { default: PersonOutline } = require('@mui/icons-material/PersonOutlineRounded');
const { useProfileImage, useProfileImageVisibility } = require('../../hooks/useProfileImage');

const internals = {};

module.exports = Image;

function Image({ ratio, src, width, height, afterLoad, onLoad, onError, alt = '', eager = false, priority = 1, placeholderBottomSpace = 0, style, ...other }) {

    const { Frame, Placeholder, IconFrame, Photo } = internals;
    const { ref, inView } = useProfileImageVisibility(eager);
    const image = useProfileImage(src, { enabled: eager || inView, priority });
    const [decodedSrc, setDecodedSrc] = useState(null);
    const photoRef = useRef(null);
    const decoded = image.src && decodedSrc === image.src;
    const message = image.status === 'missing' ? 'No photo' : image.status === 'error' ? 'Photo unavailable' : 'Loading photo';

    useLayoutEffect(() => {

        const photo = photoRef.current;
        if (image.src && photo?.complete && photo.naturalWidth > 0) {
            setDecodedSrc(image.src);
        }
    }, [image.src]);

    return (
        <Frame ref={ref} $ratio={ratio} data-photo-state={decoded ? 'loaded' : image.status === 'loaded' ? 'loading' : image.status}>
            {!decoded && <Placeholder $bottomSpace={placeholderBottomSpace} role='img' aria-label={alt ? `${alt}: ${message}` : message}>
                <IconFrame $compact={placeholderBottomSpace > 0} aria-hidden='true'><PersonOutline /></IconFrame>
                <span>{message}</span>
            </Placeholder>}
            {image.src && <Photo
                key={image.src}
                ref={photoRef}
                {...other}
                src={image.src}
                alt={alt}
                width={width}
                height={height}
                style={{ ...style, opacity: decoded ? 1 : 0 }}
                onLoad={(event) => {

                    setDecodedSrc(image.src);
                    afterLoad?.(event);
                    onLoad?.(event);
                }}
                onError={(event) => {

                    image.onError();
                    onError?.(event);
                }}
            />}
        </Frame>
    );
}

Image.propTypes = {
    ratio: T.oneOf(['1/1', '4/3', '3/4', '6/4', '4/6', '16/9', '9/16', '21/9', '9/21']),
    afterLoad: T.func,
    onLoad: T.func,
    onError: T.func,
    alt: T.string,
    src: T.string,
    width: T.any,
    height: T.any,
    style: T.object,
    eager: T.bool,
    priority: T.number,
    placeholderBottomSpace: T.number
};

internals.Frame = styled.span`
    display: block;
    position: relative;
    width: 100%;
    height: ${({ $ratio }) => $ratio ? 'auto' : '100%'};
    aspect-ratio: ${({ $ratio }) => $ratio || 'auto'};
    overflow: hidden;
    border-radius: inherit;
    line-height: 1;
`;

internals.Placeholder = styled.span`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding-bottom: ${({ $bottomSpace }) => $bottomSpace}px;
    background: linear-gradient(145deg, #f7f9fb, #edf2f6);
    color: #647384;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.4;
`;

internals.Photo = styled.img`
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

internals.IconFrame = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ $compact }) => $compact ? 40 : 56}px;
    height: ${({ $compact }) => $compact ? 40 : 56}px;
    flex-shrink: 0;
    border: 1px solid #dce4eb;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.65);
    color: #8293a4;

    svg {
        width: 30px;
        height: 30px;
    }
`;
